import { useId } from "react";
import type { FC, ReactNode } from "react";
import { Modal } from "react-bootstrap";
import type { ModalProps } from "react-bootstrap";
import { useTranslation } from "react-i18next";

import IconClose from "components/Icons/IconClose/IconClose";

// AIDEV-NOTE: Props jsou union, ne tři volitelná pole — je to jediná pojistka, že lightbox
// nezůstane bez přístupného jména. `title`/`bodyTitleId`/`ariaLabel` se vzájemně vylučují
// přes `?: never`, takže kombinace bez ani jednoho z nich `tsc` odmítne. [TD-344]
type LightboxNaming =
  | { title: ReactNode; bodyTitleId?: never; ariaLabel?: never }
  | { title?: never; bodyTitleId: string; ariaLabel?: never }
  | { title?: never; bodyTitleId?: never; ariaLabel: string };

type Props = LightboxNaming & {
  show: boolean;
  onHide: () => void;
  /** Ikona vlevo od titulku (typ 2 z návrhu). Parametr konkrétního lightboxu, neodvozuje
   *  se z ničeho — rozhodnuto 31. 8. 2026. */
  titleIcon?: ReactNode;
  /** Doplněk vlevo od křížku (typ 3 z návrhu — stopky). */
  headerAside?: ReactNode;
  /** Obsah v levé části hlavičky u lightboxu bez titulku — dnes jen tlačítko „zpět"
   *  v rozboru úlohy. Není to náhrada titulku: nepojmenovává dialog, jen zabírá místo,
   *  kde by titulek byl. Lightbox s titulkem ho nepoužívá. */
  headerStart?: ReactNode;
  /** Patička s akčními tlačítky. Bez ní se `Modal.Footer` vůbec nevykreslí — řada
   *  lightboxů žádnou nemá a prázdný pruh by přidal místo navíc. */
  footer?: ReactNode;
  /** Třída na `Modal.Footer`. Patička nese u některých lightboxů vlastní pozadí
   *  (`modal-footer--yellow-bg`) nebo zarovnání — bez tohohle by se ztratilo. */
  footerClassName?: string;
  /** Třída přímo na `Modal.Body`, ne na obalový div uvnitř — mezerníkové utility
   *  (`pb-40px` apod.) jsou `!important` a na vnořeném divu by se jen sečetly
   *  s výchozím paddingem těla, ne ho nahradily. */
  bodyClassName?: string;
  children: ReactNode;
} & Pick<ModalProps, "size" | "centered" | "backdrop" | "className" | "dialogClassName" | "scrollable">;

/**
 * Sjednocená hlavička lightboxů (TD-344). Křížek je vždy, hlavička se vykresluje vždy —
 * i prázdná (typ 1 z návrhu). Jméno dialogu zajišťuje `aria-labelledby`/`aria-label`
 * podle toho, který ze tří props přišel — react-bootstrap sám `Modal.Title` s dialogem
 * nepropojuje, proto to dělá tahle komponenta.
 */
const Lightbox: FC<Props> = ({
  show,
  onHide,
  title,
  bodyTitleId,
  ariaLabel,
  titleIcon,
  headerAside,
  headerStart,
  footer,
  footerClassName,
  bodyClassName,
  children,
  ...modalProps
}) => {
  // Vždy zavolané, i když se `title` nepoužije — Rules of Hooks nedovolují useId schovat
  // za podmínku, a generovat id ručně je přesně to, čemu se má komponenta vyhnout.
  const titleId = useId();
  const ariaLabelledBy = title !== undefined ? titleId : bodyTitleId;
  const { t } = useTranslation(["common"]);

  return (
    <Modal show={show} onHide={onHide} aria-label={ariaLabel} aria-labelledby={ariaLabelledBy} {...modalProps}>
      {/* AIDEV-NOTE: Výška hlavičky (44 px holá / 68 px s nadpisem, uzel 11400:44155) se
          odvozuje z `title` samotného, ne z dalšího propu — modifikátorová třída jde jen
          na variantu s nadpisem, holá si vystačí s bootstrapím `$modal-header-padding-y`
          (12) + 20px křížek = 44 beze změny. [TD-344] */}
      <Modal.Header className={title !== undefined ? "lightbox__header--title" : undefined}>
        <div className="lightbox__header-start">
          {title !== undefined ? (
            <h2 id={titleId} className="lightbox__title">
              {/* AIDEV-NOTE: `aria-hidden` na obalu, ne na `titleIcon` samotné — voláno je
                  s ledasčím (SVG i čistý text), a `h2` je zdroj přístupného jména dialogu
                  přes `aria-labelledby`. Bez toho by se obsah ikony propsal do jména. [TD-344] */}
              {titleIcon !== undefined ? (
                <span className="lightbox__title-icon" aria-hidden="true">
                  {titleIcon}
                </span>
              ) : null}
              {title}
            </h2>
          ) : (
            headerStart
          )}
        </div>
        <div className="lightbox__header-end">
          {headerAside}
          {/* AIDEV-NOTE: Vlastní tlačítko, ne bootstrapí `CloseButton` — ten má v tomhle
              projektu `$btn-close-width: 14px` a jde přepsat jen skrz další lokální
              přepis (viz smazaný `.exam-settings .btn-close`). `IconClose` je 20×20 podle
              návrhu bez úprav a stejnou cestou jde `PracticeFlipCard` (typ 3, mimo tenhle
              převod). Popisek bere existující `common:close`, žádný nový klíč. [TD-344] */}
          <button type="button" className="lightbox__close" onClick={onHide} aria-label={t("close", { ns: "common" })}>
            <IconClose aria-hidden="true" />
          </button>
        </div>
      </Modal.Header>
      <Modal.Body className={bodyClassName}>{children}</Modal.Body>
      {footer !== undefined ? <Modal.Footer className={footerClassName}>{footer}</Modal.Footer> : null}
    </Modal>
  );
};

export default Lightbox;
